---
title: HR & Office Manager
status: open
type: Full-Time
location: Primarily On-Site — Hancock, Michigan
compensation: $55,000 – $70,000 / year
summary: Build our HR function from the ground up — handbook, policies, hiring, and manager coaching — while owning the books, payroll, and office administration.
---

## Compensation

**$55,000 – $70,000 annually**, depending on experience, demonstrated capability, and scope of responsibility

## Benefits

- Health insurance
- Dental and vision insurance
- 401(k) with company match
- Paid Time Off (PTO)
- 7 paid holidays

## About Xeratec

At Xeratec we build custom software for the construction industry, designed around the way each contractor uniquely works. We’re a family-founded company with more than 20 years’ experience in the software space. We have 15–20 people in Hancock, which is small enough that everyone knows everyone and good work gets noticed.

We’re actively seeking an **HR & Office Manager** to develop and manage our HR policies and procedures, along with our bookkeeping and office administration.

## Role Summary

**Reports to:** CEO

As we have grown, we have never established a dedicated HR function. Now that we have grown to the point where we need one, we are seeking the ideal candidate. You will have the opportunity to build the HR role from the ground up: the employee handbook, the policies that guide and govern the business, and the documentation that supports it. You will work alongside leadership to build a culture of accountability, where expectations are clear, problems are addressed early, and employees are clear on the company’s expectations. Our leaders will maintain responsibility for managing their own people.

This role sets the standards they manage by, the processes they follow, and the coaching to do it well. Outside employment counsel is available as needed.

The other half of the role is bookkeeping and office administration. You will own the day-to-day books and payroll and keep them accurate and on time, so the CEO and our CPA always have clean numbers to make decisions from.

## Who This Role Is For

- Someone who is calm, fair, and direct, with a kind but clear delivery
- Someone who can keep pay, performance, and personal situations confidential
- Someone who deals with issues promptly instead of letting them sit
- Someone who takes ownership without needing step-by-step direction
- Someone who is careful with numbers and details

## Key Responsibilities

### Human Resources

- Own and maintain the employee handbook and company policies, with legal review as needed
- Oversee hiring, onboarding, and offboarding in a structured and consistent manner
- Maintain personnel files, I-9s, and other required records, and stay current on employment law requirements, with outside counsel as needed
- Administer benefits, open enrollment, and PTO
- Coach and support managers through performance conversations, documentation, and corrective action
- Keep quarterly check-ins and annual reviews on schedule
- Be the first point of contact for employee questions and concerns
- Spot recurring people or management problems and bring leadership practical recommendations that fix the cause

### Bookkeeping & Payroll

- Code transactions, manage receipts, and handle accounts payable
- Follow up on receivables and past-due accounts
- Reconcile bank and credit card accounts monthly and support a clean month-end close
- Process payroll and 401(k) contributions
- Support our CPA with quarterly and annual tax preparation
- Maintain a calendar of financial and compliance deadlines

### Office Administration

- Manage mail, deposits, records, and office supplies
- Help coordinate travel, meetings, and company events
- Communicate with vendors and outside partners
- Take on special projects for leadership as needed

## What Success Looks Like in the First Year

- The employee handbook is in place, every employee has read it, and managers are using it
- Hiring and onboarding follow the same process every time
- Managers address issues early, document them correctly, and come to you before a problem grows
- Leadership hears about concerning patterns early and gets practical guidance on how to address them
- Books are accurate, month-end closes on time, and payroll and filings are never a scramble
- Personnel and compliance records are complete and current

## Qualifications (Required)

- 3+ years of hands-on human resources experience, such as hiring, onboarding, benefits, employee relations, or policy work
- SHRM-CP or PHR certification
- Experience handling sensitive employee matters calmly and confidentially
- Comfort with numbers and detail (bookkeeping or payroll experience is a plus, and we will train you on our books if needed)
- Comfort working with QuickBooks (or willingness to learn)
- Clear written and verbal communication skills
- Basic proficiency with Microsoft Office and/or Google Workspace
- Reliable transportation and ability to work primarily on-site in Hancock, Michigan

**A college degree is not required.** Beyond credentials, we value reliability, sound judgment, follow-through, and a genuine desire to do good work.

## How to Apply

Send your resume to careers@xeratec.com. In your note, tell us briefly about a time you had to deliver news someone did not want to hear. A few sentences is plenty.

_Xeratec is an equal opportunity employer. We consider all qualified applicants without regard to race, color, religion, sex, national origin, age, disability, veteran status, or any other status protected by law._
